import React, { useContext, useState } from 'react';
import { motion } from 'framer-motion';
import { LanguageContext } from '../hooks/useLanguage';
import { contactSection } from '../data/content';

const Contact: React.FC = () => {
  const { lang } = useContext(LanguageContext);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(false);
    const form = e.currentTarget;
    const data = new FormData(form);
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as any).toString(),
      });
      if (res.ok) {
        setSent(true);
        form.reset();
        setTimeout(() => setSent(false), 5000);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '16px 20px',
    border: '1px solid rgba(255,255,255,0.06)',
    background: '#1A1A1A',
    fontSize: '15px',
    outline: 'none',
    transition: 'border 0.2s',
    fontFamily: 'var(--font-body)',
    color: '#F0F0F0',
  };

  return (
    <section
      id="contact"
      style={{ background: '#0D0D0D', padding: 'clamp(60px, 8vw, 120px) 0' }}
    >
      <div className="container">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '80px',
            alignItems: 'start',
          }}
          className="contact-grid"
        >
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span style={{
              fontFamily: 'var(--font-body)',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '5px',
              textTransform: 'uppercase',
              color: '#C8A96E',
              display: 'block',
              marginBottom: '12px',
            }}>
              {contactSection.label[lang]}
            </span>
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(26px, 3.5vw, 48px)',
              fontWeight: 700,
              letterSpacing: '-0.5px',
              lineHeight: 1.1,
              color: '#FFFFFF',
              marginBottom: '48px',
            }}>
              {contactSection.title[lang]}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {[
                { label: 'Email', value: 'info@vatiocentral.com', href: 'mailto:info@vatiocentral.com' },
                { label: lang === 'es' ? 'Teléfono' : lang === 'en' ? 'Phone' : 'Téléphone', value: '+34 936 943 568', href: 'tel:+34936943568' },
                { label: lang === 'es' ? 'Ubicación' : lang === 'en' ? 'Location' : 'Adresse', value: 'España', href: '#' },
              ].map((item) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  whileHover={{ borderColor: 'rgba(200,169,110,0.4)' }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    color: '#F0F0F0',
                    textDecoration: 'none',
                    padding: '20px 24px',
                    background: '#1A1A1A',
                    border: '1px solid rgba(255,255,255,0.06)',
                    transition: 'border-color 0.3s',
                  }}
                >
                  <div>
                    <div style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '11px',
                      color: '#C8A96E',
                      letterSpacing: '3px',
                      textTransform: 'uppercase',
                      marginBottom: '4px',
                    }}>{item.label}</div>
                    <div style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#FFFFFF',
                    }}>{item.value}</div>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form
              name="contact"
              method="post"
              data-netlify="true"
              data-netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              style={{
                background: '#1A1A1A',
                padding: 'clamp(32px, 3vw, 56px)',
                border: '1px solid rgba(255,255,255,0.06)',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}
            >
              {/* Required hidden fields for Netlify Forms */}
              <input type="hidden" name="form-name" value="contact" />
              <input type="hidden" name="bot-field" style={{ display: 'none' }} />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
                <input
                  required
                  type="text"
                  name="name"
                  placeholder={contactSection.namePlaceholder[lang]}
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = 'rgba(200,169,110,0.5)')}
                  onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.06)')}
                />
                <input
                  required
                  type="email"
                  name="email"
                  placeholder={contactSection.emailPlaceholder[lang]}
                  style={inputStyle}
                  onFocus={(e) => (e.target.style.borderColor = 'rgba(200,169,110,0.5)')}
                  onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.06)')}
                />
              </div>
              <input
                type="tel"
                name="phone"
                placeholder={contactSection.phonePlaceholder[lang]}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = 'rgba(200,169,110,0.5)')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.06)')}
              />
              <textarea
                required
                rows={5}
                name="message"
                placeholder={contactSection.messagePlaceholder[lang]}
                style={{ ...inputStyle, resize: 'vertical' }}
                onFocus={(e) => (e.target.style.borderColor = 'rgba(200,169,110,0.5)')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(255,255,255,0.06)')}
              />
              <motion.button
                type="submit"
                whileHover={{ background: '#C8A96E', color: '#0D0D0D' }}
                whileTap={{ scale: 0.98 }}
                style={{
                  padding: '18px',
                  background: 'transparent',
                  color: '#C8A96E',
                  border: '1px solid #C8A96E',
                  fontFamily: 'var(--font-body)',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  marginTop: '8px',
                }}
              >
                {sent
                  ? (lang === 'es' ? '✓ Enviado' : lang === 'en' ? '✓ Sent' : '✓ Envoyé')
                  : error
                  ? (lang === 'es' ? '✕ Error — reintenta' : lang === 'en' ? '✕ Error — try again' : '✕ Erreur — réessayer')
                  : contactSection.button[lang]}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
};

export default Contact;
